import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, message } from 'antd';
import { Box, Text, Button } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import { CancelButton } from 'utils/commonStyled';

const { TextArea } = Input;

const formatINR = (value) => {
	const n = Number(value);
	if (value == null || Number.isNaN(n)) return '-';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const columns = [
	{
		title: 'Trip ID',
		dataIndex: 'display_id',
		key: 'display_id',
		render: (t, record) => t || record.trip_id || '-',
	},
	{ title: 'Date', dataIndex: 'date', key: 'date', render: (t) => t || '-' },
	{ title: 'Source', dataIndex: 'origin', key: 'origin', render: (t) => t || '-' },
	{ title: 'Destination', dataIndex: 'destination', key: 'destination', render: (t) => t || '-' },
	{
		title: 'Vehicle No',
		dataIndex: 'vehicle_number',
		key: 'vehicle_number',
		render: (t) => t || '-',
	},
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicle_type',
		key: 'vehicle_type',
		render: (t) => t || '-',
	},
	{ title: 'Driver', dataIndex: 'driver', key: 'driver', render: (t) => t || '-' },
	{ title: 'Trip Type', dataIndex: 'trip_type', key: 'trip_type', render: (t) => t || '-' },
	{
		title: 'Distance(km)',
		dataIndex: 'distance_km',
		key: 'distance_km',
		render: (t) => t ?? '-',
	},
	{ title: 'Base Fare', dataIndex: 'base_fare', key: 'base_fare', render: formatINR },
	{ title: 'Toll/Extra', dataIndex: 'toll_extra', key: 'toll_extra', render: formatINR },
	{ title: 'Total', dataIndex: 'total', key: 'total', render: formatINR },
];

/**
 * Client Invoice Regeneration — Step 1 (trip selection). AR mirror of
 * TripSelectionStep for the regenerate flow: shows every closed trip for the
 * invoice's client/project; trips already on this invoice are pre-selected, and
 * the user can check/uncheck others. Proceeding hands the selected trips + an
 * optional reason to Step 2 (the rich invoice form), same pattern as Create Invoice.
 */
export default function RegenerateInvoiceForm({ invoice, trips, onProceed }) {
	const navigate = useNavigate();

	const allTrips = useMemo(() => trips || [], [trips]);
	const preselectedIds = useMemo(
		() =>
			allTrips
				.filter((t) => t.is_on_invoice)
				.map((t) => t.trip_id)
				.filter(Boolean),
		[allTrips]
	);
	const [selectedIds, setSelectedIds] = useState(preselectedIds);
	const [reason, setReason] = useState('');

	useEffect(() => {
		setSelectedIds(preselectedIds);
	}, [preselectedIds]);

	const selectedTotal = useMemo(
		() =>
			allTrips
				.filter((t) => selectedIds.includes(t.trip_id))
				.reduce((sum, t) => sum + Number(t.amount || 0), 0),
		[allTrips, selectedIds]
	);

	const rowSelection = {
		selectedRowKeys: selectedIds,
		onChange: (keys) => setSelectedIds(keys),
	};

	const handleProceed = () => {
		if (!selectedIds.length) {
			message.error('Please select at least one trip.');
			return;
		}
		const selectedTrips = allTrips.filter((t) => selectedIds.includes(t.trip_id));
		onProceed({ selectedTrips, reason: reason || undefined });
	};

	return (
		<Box>
			<Box padding="1.5rem 2rem" background="#fff" mb={2}>
				<Row gutter={16} justify="space-between">
					<Col lg={8}>Invoice Number : {invoice?.invoice_no || '-'}</Col>
					<Col lg={8}>Client : {invoice?.client || '-'}</Col>
					<Col lg={8}>Revision : {invoice?.version ?? '-'}</Col>
				</Row>
				<Row gutter={16} justify="space-between" style={{ marginTop: 12 }}>
					<Col lg={8}>Billing Period : {invoice?.billing_period || '-'}</Col>
					<Col lg={8}>
						Current Total :{' '}
						{formatINR(invoice?.grand_total_display ?? invoice?.grand_total)}
					</Col>
				</Row>
			</Box>

			<Box backgroundColor="#f5f5f5" padding="1.5rem" mb={2}>
				<Row>
					<Col lg={12}>
						<Text>Total selected Trips: {selectedIds.length}</Text>
					</Col>
					<Col lg={12}>
						<Text>Total Selected Amount: {formatINR(selectedTotal)}</Text>
					</Col>
				</Row>
			</Box>

			<AntdTable
				rowKey="trip_id"
				rowSelection={rowSelection}
				columns={columns}
				dataSource={allTrips}
				pagination={false}
			/>

			<Box padding="1.5rem 2rem" background="#fff" mt={2}>
				<Text style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
					Reason (optional)
				</Text>
				<TextArea
					rows={3}
					value={reason}
					maxLength={512}
					onChange={(e) => setReason(e.target.value)}
					placeholder="Reason for regenerating this invoice…"
				/>
			</Box>

			<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem" mt={2}>
				<CancelButton type="button" onClick={() => navigate('/client-invoices')}>
					Cancel
				</CancelButton>
				<Button
					borderRadius="4px"
					backgroundColor="#18405d"
					type="button"
					onClick={handleProceed}
					disabled={!selectedIds.length}
				>
					Proceed to Invoice Regeneration
				</Button>
			</Box>
		</Box>
	);
}

RegenerateInvoiceForm.propTypes = {
	invoice: PropTypes.object,
	trips: PropTypes.array,
	onProceed: PropTypes.func.isRequired,
};
